import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { PASSES_LANDING } from "@/settings/constant";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { DISPLAY_GRADE_PARAM, GRADE_LABEL, getPassMainProducts, getPassProducts, isValidPassGrade, matchesPassGrade, passGradeFromSchoolGrade } from "src/api/membership";
import { Subjects, SubjectsWithString } from "src/api/product";
import { useHomeSwitchStore } from "src/store/homeSwitch.store";
export const usePassTab = (options)=>{
    const router = useRouter();
    const { subject  } = router.query;
    const { homeType  } = useHomeSwitchStore();
    const { user , isLoggedIn  } = useAuthStore();
    var _options_pageType;
    // 외부에서 전달된 pageType이 있으면 사용, 없으면 store의 homeType 사용
    const pageType = (_options_pageType = options === null || options === void 0 ? void 0 : options.pageType) !== null && _options_pageType !== void 0 ? _options_pageType : homeType;
    // 학생 학년 탭 상태 — 우선순위: URL 쿼리 `?grade=` > 로그인 사용자 학년(schoolGrade+education) > GRADE1.
    // 여기서 URL을 자동 push하지 않는다: 그러면 `?grade=GRADE23` 딥링크 진입이 덮인다. 갱신은 탭 클릭 시에만.
    // 사용자 학년은 학생 페이지에서만 반영한다(선생님 페이지에는 학년 탭 자체가 없음).
    // education(학교급)을 함께 넘겨야 중2·중3이 고2·3 탭으로 가지 않는다.
    const userGrade = pageType === "student" ? passGradeFromSchoolGrade(user === null || user === void 0 ? void 0 : user.schoolGrade, user === null || user === void 0 ? void 0 : user.education) : null;
    const studentGrade = isValidPassGrade(router.query.grade) ? router.query.grade : userGrade !== null && userGrade !== void 0 ? userGrade : "GRADE1";
    // 선생님 모드에서 로그인한 사용자의 기본 카테고리 결정
    // - 학생/학부모: ALL
    // - 선생님/원장: mainSubject 또는 EN
    const getDefaultCategory = ()=>{
        if (!isLoggedIn) return Subjects.EN;
        if ((user === null || user === void 0 ? void 0 : user.job) === "student" || (user === null || user === void 0 ? void 0 : user.job) === "parent") return Subjects.ALL;
        if (user === null || user === void 0 ? void 0 : user.mainSubject) return user.mainSubject;
        return Subjects.EN;
    };
    // 학생 모드에서는 항상 ALL, 선생님 모드에서는 URL의 subject 또는 사용자 기반 기본값
    const passCategory = pageType === "student" ? Subjects.ALL : subject ? subject : getDefaultCategory();
    // 학생 페이지에서는 subjectCode를 보내지 않음 (전과목만 있으므로)
    const shouldSendSubjectCode = pageType !== "student";
    const { data: passResponse , isSuccess: isPassProductsSuccess  } = useQuery([
        "getPassProducts",
        passCategory,
        pageType
    ], ()=>{
        const params = {
            ...shouldSendSubjectCode && {
                subjectCode: passCategory
            },
            pageType
        };
        return getPassProducts(params);
    }, {
        retry: 1
    });
    // 메인 상품: 학생은 학년을 서버로 보내 카드 필터·구획 제목 계산을 BE에 맡긴다.
    // 전제: displayGrade는 어드민이 항상 저작한다 (groupMapping과 동일한 운영 정책).
    // 학년이 key에 있어 탭 전환 시 해당 학년을 조회하고, 재방문은 staleTime 내 캐시로 즉시 렌더된다.
    const { data: passMainItemResponse , isSuccess: isAnnualPassProductsSuccess  } = useQuery([
        "getPassMainProducts",
        passCategory,
        pageType,
        pageType === "student" ? studentGrade : null
    ], ()=>{
        const params = {
            ...shouldSendSubjectCode && {
                subjectCode: passCategory
            },
            pageType,
            // 파라미터 문자는 DISPLAY_GRADE_PARAM 고정 — GRADE_LABEL(U+2219)을 보내면 0건이 된다
            ...pageType === "student" && {
                displayGrade: DISPLAY_GRADE_PARAM[studentGrade]
            }
        };
        return getPassMainProducts(params);
    }, {
        retry: 1,
        staleTime: 60000,
        // true 금지: isSuccess가 유지되면 isEmptyResult가 refetch 중 '결과 없음'으로 오판한다.
        keepPreviousData: false
    });
    const handlePassTab = useCallback((subject)=>{
        // 현재 페이지 타입(teacher/student)을 유지하면서 subject만 변경
        router.push("".concat(PASSES_LANDING, "/").concat(pageType, "?subject=").concat(subject), undefined, {
            shallow: true
        });
        // mx-108 이용권 과목 탭 클릭시
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": "Pass Product",
            "Tab Name": SubjectsWithString[subject]
        });
    }, [
        router,
        pageType
    ]);
    // 학생 학년 탭 클릭 시에만 URL 갱신. 기존 쿼리(redirect 등)를 스프레드로 보존한다.
    const handleStudentGradeTab = (grade)=>{
        router.push({
            pathname: router.pathname,
            query: {
                ...router.query,
                grade
            }
        }, undefined, {
            shallow: true
        });
        // mx-108 이용권 학년 탭 클릭 시. GRADE 키는 SubjectsWithString에 없어 GRADE_LABEL로 라벨 매핑(없으면 undefined 전송됨)
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": "Pass Product",
            "Tab Name": GRADE_LABEL[grade]
        });
    };
    //엑스퍼트 이용권을 먼저 보여주기 위한 order sorting
    const groupBySubjectData = useMemo(()=>{
        if (!passResponse) return null;
        // 학생 모드에서는 모든 과목의 products를 합쳐서 보여줌
        if (pageType === "student") {
            const allProducts = passResponse.data.flatMap((group)=>group.products);
            // categoryTitle 기준으로 중복 제거 및 합치기 (react-query 캐시 원본 mutate 방지 위해 복사본으로 작업)
            const mergedProductsMap = new Map();
            allProducts.forEach((product)=>{
                const existing = mergedProductsMap.get(product.categoryTitle);
                if (existing) {
                    // 같은 카테고리면 items 합치기 (id 기준 중복 제거)
                    const existingItemIds = new Set(existing.items.map((item)=>item.id));
                    const newItems = product.items.filter((item)=>!existingItemIds.has(item.id));
                    existing.items = [
                        ...existing.items,
                        ...newItems
                    ];
                } else {
                    mergedProductsMap.set(product.categoryTitle, {
                        ...product,
                        items: [
                            ...product.items
                        ]
                    });
                }
            });
            const productsOrdering = Array.from(mergedProductsMap.values()).sort((a, b)=>a.categoryOrder - b.categoryOrder).map((product)=>({
                    ...product,
                    // 선택 학년으로 items 선필터 (grade 누락 아이템은 matchesPassGrade가 true → 모든 탭 노출)
                    // TODO: grade 누락 아이템 노출 정책은 BE 확인 대기 중이라 달라질 수 있음 — 정책 확정 시 matchesPassGrade 수정
                    items: product.items.filter((item)=>matchesPassGrade(item.grade, studentGrade)).sort((a, b)=>a.order - b.order)
                }))// 필터 후 items가 0개가 된 카테고리 그룹 제거 — 남기면 상품 없는 카테고리 헤더만 노출됨
            .filter((product)=>product.items.length > 0);
            return {
                subject: SubjectsWithString[Subjects.ALL],
                // 결과가 0건이어도 null이 아닌 빈 products 객체 반환. null이면 '준비중' 다이얼로그가 오발화한다.
                products: productsOrdering
            };
        }
        const filterSubject = passResponse.data.filter((group)=>group.subject === SubjectsWithString[passCategory])[0];
        if (!filterSubject) return null;
        // 상위 하위 아이템들 order값으로 order sorting (react-query 캐시 원본 mutate 방지 위해 product도 복사)
        const productsOrdering = [
            ...filterSubject.products
        ].sort((a, b)=>a.categoryOrder - b.categoryOrder).map((product)=>({
                ...product,
                items: [
                    ...product.items
                ].sort((a, b)=>a.order - b.order)
            }));
        return {
            ...filterSubject,
            products: productsOrdering
        };
    // studentGrade가 deps에 있어야 학년 탭 전환 시 필터가 재계산된다.
    }, [
        passResponse,
        pageType,
        passCategory,
        studentGrade
    ]);
    var _passMainItemResponse_data, _group_groupTitle, _group_groupSubTitle;
    // 메인 상품 진열 구획. 구획 순서·items의 seq 순서는 BE 응답 그대로 (재정렬 금지).
    // 학년 필터·구획 제목 계산은 서버 응답이 이미 끝낸 상태라 FE 가공이 없다.
    // groupMapping 미저작(null) 구획은 노출하지 않는다 — 이 필터가 구 구조(flat 배열) 응답 방어를 겸한다.
    const mainGroups = useMemo(()=>{
        return ((_passMainItemResponse_data = passMainItemResponse === null || passMainItemResponse === void 0 ? void 0 : passMainItemResponse.data) !== null && _passMainItemResponse_data !== void 0 ? _passMainItemResponse_data : []).filter((group)=>group.groupMapping != null).map((group)=>({
                // 탭 전환 시 swiper 내부 상태가 섞이지 않게 구획 아이덴티티를 key로 쓴다
                key: "group-".concat(group.groupMapping),
                // 카드의 동명 필드는 어드민 저작 원본이라 값이 어긋난다. 비면 미노출
                title: (_group_groupTitle = group.groupTitle) !== null && _group_groupTitle !== void 0 ? _group_groupTitle : "",
                subTitle: (_group_groupSubTitle = group.groupSubTitle) !== null && _group_groupSubTitle !== void 0 ? _group_groupSubTitle : "",
                items: group.items
            })).filter((group)=>group.items.length > 0);
    }, [
        passMainItemResponse
    ]);
    return {
        handlePassTab,
        handleStudentGradeTab,
        studentGrade,
        passCategory,
        groupBySubjectData,
        isPassProductsSuccess,
        isAnnualPassProductsSuccess,
        mainGroups
    };
};
